// @flow
import { type ActionType, createAction, handleActions } from 'redux-actions';
import produce from 'immer';
import { applyPenders } from 'lib/common';
import * as CartAPI from 'lib/api/me/cart';

/* ACTION TYPE */
const GET_CART = 'cart/GET_CART';
const GET_AS_NON_REGISTERED_CART = 'cart/GET_AS_NON_REGISTERED_CART';
const ALL_CHECKED_CART = 'cart/ALL_CHECKED_CART';
const UPDATE_CHECKED_CART = 'cart/UPDATE_CHECKED_CART';
const ADD_ITEM_CART = 'cart/ADD_ITEM_CART'
const ADD_ITEM_AS_NON_REGISTERED_CART = 'cart/ADD_ITEM_AS_NON_REGISTERED_CART'
const EMPTY_CART = 'cart/EMPTY_CART'
const EMPTY_AS_NON_REGISTERED_CART = 'cart/EMPTY_AS_NON_REGISTERED_CART'
const COUNT_ITEMS_CART = 'cart/COUNT_ITEMS_CART'
const DELETE_ITEM_CART = 'cart/DELETE_ITEM_CART'
const DELETE_ITEM_AS_NON_REGISTERED_CART = 'cart/DELETE_ITEM_AS_NON_REGISTERED_CART'
const UPDATE_ITEM_CART = 'cart/UPDATE_ITEM_CART'
const UPDATE_ITEM_AS_NON_REGISTERED_CART = 'cart/UPDATE_ITEM_AS_NON_REGISTERED_CART'
const CHECKOUT_CART = 'cart/CHECKOUT_CART'
const CHECKOUT_AS_NON_REGISTERED_CART = 'cart/CHECKOUT_AS_NON_REGISTERED_CART'

/* ACTION CREATOR */
const getCart = createAction(GET_CART, CartAPI.getForMe);
const getAsNonRegisteredCart = createAction(GET_AS_NON_REGISTERED_CART, CartAPI.getAsNonRegisteredForMe);
const allCheckedCart = createAction(ALL_CHECKED_CART);
const updateCheckedCart = createAction(UPDATE_CHECKED_CART);
const addItemCart = createAction(ADD_ITEM_CART, CartAPI.addItemForMe);
const addItemAsNonRegisteredCart = createAction(ADD_ITEM_AS_NON_REGISTERED_CART, (payload) => payload);
const emptyCart = createAction(EMPTY_CART, CartAPI.emptyForMe);
const emptyAsNonRegisteredCart = createAction(EMPTY_AS_NON_REGISTERED_CART);
const countItemsCart = createAction(COUNT_ITEMS_CART, CartAPI.countItemsForMe);
const deleteItemCart = createAction(DELETE_ITEM_CART, CartAPI.deleteItemForMe);
const deleteItemAsNonRegisteredCart = createAction(DELETE_ITEM_AS_NON_REGISTERED_CART, (payload) => payload);
const updateItemCart = createAction(UPDATE_ITEM_CART, CartAPI.updateItemForMe);
const updateItemAsNonRegisteredCart = createAction(UPDATE_ITEM_AS_NON_REGISTERED_CART, (payload) => payload);
const checkoutCart = createAction(CHECKOUT_CART, CartAPI.checkoutForMe);
const checkoutAsNonRegisteredCart = createAction(CHECKOUT_AS_NON_REGISTERED_CART, CartAPI.checkoutAsNonRegisteredForMe);

/* ACTION FLOW TYPE */
type GetCartAction = ActionType<typeof getCart>;
type GetAsNonRegisteredCartAction = ActionType<typeof getAsNonRegisteredCart>;
type AllCheckedCartAction = ActionType<typeof allCheckedCart>;
type UpdateCheckedCartAction = ActionType<typeof updateCheckedCart>;
type AddItemCartAction = ActionType<typeof addItemCart>;
type AddItemAsNonRegisteredCartAction = ActionType<typeof addItemAsNonRegisteredCart>;
type EmptyCartAction = ActionType<typeof emptyCart>;
type EmptyAsNonRegisteredCartAction = ActionType<typeof emptyAsNonRegisteredCart>;
type CountItemsCartAction = ActionType<typeof countItemsCart>;
type DeleteItemCartAction = ActionType<typeof deleteItemCart>;
type DeleteItemAsNonRegisteredCartAction = ActionType<typeof deleteItemAsNonRegisteredCart>;
type UpdateItemCartAction = ActionType<typeof updateItemCart>;
type UpdateItemAsNonRegisteredCartAction = ActionType<typeof updateItemAsNonRegisteredCart>;
type CheckoutCartAction = ActionType<typeof checkoutCart>;
type CheckoutAsNonRegisteredCartAction = ActionType<typeof checkoutAsNonRegisteredCart>;

/* ACTION CREATORS INTERFACE */
export interface CartActionCreators {
  getCart(payload: GetCartAction): any;
  getAsNonRegisteredCart(payload: GetAsNonRegisteredCartAction): any;
  allCheckedCart(payload: AllCheckedCartAction): any;
  updateCheckedCart(payload: UpdateCheckedCartAction): any;
  addItemCart(payload: AddItemCartAction): any;
  addItemAsNonRegisteredCart(payload: AddItemAsNonRegisteredCartAction): any;
  emptyCart(payload: EmptyCartAction): any;
  emptyAsNonRegisteredCart(payload: EmptyAsNonRegisteredCartAction): any;
  countItemsCart(payload: CountItemsCartAction): any;
  deleteItemCart(payload: DeleteItemCartAction): any;
  deleteItemAsNonRegisteredCart(payload: DeleteItemAsNonRegisteredCartAction): any;
  updateItemCart(payload: UpdateItemCartAction): any;
  updateItemAsNonRegisteredCart(payload: UpdateItemAsNonRegisteredCartAction): any;
  checkoutCart(payload: CheckoutCartAction): any;
  checkoutAsNonRegisteredCart(payload: CheckoutAsNonRegisteredCartAction): any;
}

/* EXPORT ACTION CREATORS */
export const actionCreators: CartActionCreators = {
  getCart,
  getAsNonRegisteredCart,
  allCheckedCart,
  updateCheckedCart,
  addItemCart,
  addItemAsNonRegisteredCart,
  emptyCart,
  emptyAsNonRegisteredCart,
  countItemsCart,
  deleteItemCart,
  deleteItemAsNonRegisteredCart,
  updateItemCart,
  updateItemAsNonRegisteredCart,
  checkoutCart,
  checkoutAsNonRegisteredCart,
};

/* STATE TYPES */
export type Cart = {
  address: any,
  currency: any,
  errors: any,
  items: any,
  language: any,
  shipments: any,
  status: string,
  tax: any,
  total: any,
  allChecked: boolean,
  applicableCoupons: any,
  checkout: any,
};

/* INITIAL STATE */
const initialState: Cart = {
  address: {
    shipping: {
      address1: '',
      address2: '',
      city: '',
      company: '',
      country: {
        code: '',
        name: ''
      },
      mobile: '',
      name: {
        first: '',
        last: '',
        full: '',
      },
      phone: '',
      postcode: '',
      state: '',
    },
    billing: {
      address1: '',
      address2: '',
      city: '',
      company: '',
      country: {
        code: '',
        name: ''
      },
      mobile: '',
      name: {
        first: '',
        last: '',
        full: '',
      },
      phone: '',
      postcode: '',
      state: '',
    },
  },
  primary: {
    address1: '',
    address2: '',
    city: '',
    company: '',
    country: {
      code: '',
      name: ''
    },
    mobile: '',
    name: {
      first: '',
      last: '',
      full: '',
    },
    phone: '',
    postcode: '',
    state: '',
    email: '',
  },
  currency: {},
  errors: [],
  items: [],
  language: {},
  shipments: [],
  status: '',
  tax: {},
  total: {
    amount: {
      converted: '',
      convertedRow: 0,
      formatted: '',
      raw: 0
    },
    discounted: {},
    items: {
      discounted: {
        converted: ''
      },
      price: {
        sale: {
          converted: ''
        },
        original: {
          converted: ''
        }
      },
      taxed: {},
    },
    price: {
      sale: {
        converted: '',
      }
    },
    shipping: {
      fee: {
        sale: {
          converted: '',
        }
      }
    },
    taxed: {
      converted: ''
    },
    taxes: {},
  },
  allChecked: true,
  applicableCoupons: [],
  checkout: {},
};

/* REDUCER */
const reducer = handleActions(
  {
    [GET_CART]: (state, action: GetCartAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.address = action.payload.address;
        draft.primary = action.payload.primary;
        draft.currency = action.payload.currency;
        draft.errors = action.payload.errors;
        draft.items = action.payload.items.map(item => ({
          ...item,
          checked: true,
        }));
        draft.language = action.payload.language;
        draft.shipments = action.payload.shipments;
        draft.status = action.payload.status;
        draft.tax = action.payload.tax;
        draft.total = action.payload.total;
        draft.allChecked = true;
        draft.applicableCoupons = action.payload.applicableCoupons;
      });
    },
    [GET_AS_NON_REGISTERED_CART]: (state, action: GetAsNonRegisteredCartAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.address = action.payload.address;
        draft.primary = action.payload.primary;
        draft.currency = action.payload.currency;
        draft.errors = action.payload.errors;
        draft.items = action.payload.items.map(item => ({
          ...item,
          id: item._id,
          checked: true,
        }));
        draft.language = action.payload.language;
        draft.shipments = action.payload.shipments;
        draft.status = action.payload.status;
        draft.tax = action.payload.tax;
        draft.total = action.payload.total;
        draft.allChecked = true;
        draft.applicableCoupons = action.payload.applicableCoupons;
      });
    },
    [ALL_CHECKED_CART]: (state, action: AllCheckedCartAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.items = state.items.map(item => ({
          ...item,
          checked: !state.allChecked,
        }));
        draft.allChecked = !state.allChecked;
      });
    },
    [UPDATE_CHECKED_CART]: (state, action: UpdateCheckedCartAction) => {
      return produce(state, draft => {
        if (!action) return;
        const index = action.payload;
        draft.items[index].checked = !state.items[index].checked;
      });
    },
    [ADD_ITEM_CART]: (state, action: AddItemCartAction) => {
      return produce(state, draft => {
        if (!action) return;
        draft.items.push(action.payload.items);
      });
    },
    [ADD_ITEM_AS_NON_REGISTERED_CART]: (state, { payload }) => {
      return produce(state, draft => {
        window.CacheBy.LocalCart.addItem({
          product: payload.product,
          variant: payload.variant,
          quantity: payload.quantity,
          shippingMethod: payload.shippingMethod,
        });
      });
    },
    [DELETE_ITEM_AS_NON_REGISTERED_CART]: (state, { payload }) => {
      return produce(state, draft => {
        window.CacheBy.LocalCart.deleteItem(payload.itemId);
        draft.items = state.items.filter(( item ) => {
          return item._id !== payload.itemId;
        });
      });
    },
    [EMPTY_AS_NON_REGISTERED_CART]: (state, action: EmptyAsNonRegisteredCartAction) => {
      window.CacheBy.LocalCart.empty();
      return produce(state, draft => {
        if (!action) return;
        draft.items = [];
      });
    },
    [UPDATE_ITEM_AS_NON_REGISTERED_CART]: (state, { payload }) => {
      return produce(state, draft => {
        window.CacheBy.LocalCart.updateItem(payload.itemId, {
          quantity: payload.quantity,
        });
        draft.items = state.items.map((item) => {
          if (item._id === payload.itemId) {
            item.quantity = payload.quantity;
          }
          return item;
        });
        draft.items = state.items.map((item) => {
          if (item._id === payload.itemId) {
            item.quantity = payload.quantity;
          }
          return item;
        });
      });
    },
  },
  initialState,
);

export default applyPenders(reducer, [
  {
    type: GET_CART,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {
        draft.address = data.address;
        draft.primary = data.primary;
        draft.currency = data.currency;
        draft.errors = data.errors;
        draft.items = data.items.map(item => ({
          ...item,
          checked: true,
        }));
        draft.language = data.language;
        draft.shipments = data.shipments;
        draft.status = data.status;
        draft.tax = data.tax;
        draft.total = data.total;
        draft.allChecked = true;
        draft.applicableCoupons = data.applicableCoupons;
      });
    },
  },
  {
    type: GET_AS_NON_REGISTERED_CART,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {
        draft.address = data.address;
        draft.primary = data.primary;
        draft.currency = data.currency;
        draft.errors = data.errors;
        draft.items = data.items.map(item => ({
          ...item,
          id: item._id,
          checked: true,
        }));
        draft.language = data.language;
        draft.shipments = data.shipments;
        draft.status = data.status;
        draft.tax = data.tax;
        draft.total = data.total;
        draft.allChecked = true;
        draft.applicableCoupons = data.applicableCoupons;
      });
    },
  },
  {
    type: EMPTY_CART,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {
        draft.items = [];
      });
    },
  },
  {
    type: DELETE_ITEM_CART,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {
        draft.items = state.items.filter(( item ) => {
          return item.id !== data;
        });
      });
    },
  },
  {
    type: UPDATE_ITEM_CART,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {
        draft.items = state.items.map((item) => {
          if (item.id === data.id) {
            item.quantity = data.quantity;
          }
          return item;
        });
      });
    },
  },
  {
    type: CHECKOUT_CART,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {
        draft.checkout = data;
      });
    },
  },
  {
    type: CHECKOUT_AS_NON_REGISTERED_CART,
    onSuccess: (state, { payload: { data } }) => {
      return produce(state, draft => {
        draft.checkout = data;
      });
    },
  },
]);



// WEBPACK FOOTER //
// ./src/store/modules/cart.js